import axios from "axios";
import { FieldInput } from "components/fieldInput";
import { ViewContext } from "components/lib";
import { Form, Formik } from "formik";
import { Fragment, useContext, useEffect, useState, useCallback } from "react";
import * as Yup from "yup";
import { CARRIER_OPTIONS } from "helpers/constants";
import Loader from "./images/loader.gif";
import styles from "./styles.module.scss";

export function CreatePolicyModal({ showModal, setShowModal, onPolicyCreated }) {
  const context = useContext(ViewContext);
  const [loading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const validationSchema = Yup.object().shape({
    agency_id: Yup.string().required("Agency is required!"),
    company_id: Yup.string().required("Company is required!"),
    insured_name: Yup.string().required("Insured Name is required!"),
    policy_number: Yup.string().required("Policy Number is required!"),
    carrier: Yup.string().required("Carrier is required!"),
    premium_amount: Yup.number()
      .required("Premium Amount is required!")
      .min(0, "Premium Amount must be 0 or greater"),
    effective_date: Yup.date().required("Effective Date is required!"),
  });

  // Fetch agencies for dropdown
  const fetchAgencies = useCallback(async () => {
    try {
      const response = await axios.get("/api/account/all/minimal");
      const agencies = response.data.data;
      setAgenciesOptions(
        agencies.map((agency) => ({
          label: agency.name,
          value: agency.id,
        }))
      );
    } catch (error) {
      console.error("Error fetching agencies:", error);
      context.notification.show("Failed to load agencies", "error", true, "toast", "x-circle");
    }
  }, [context.notification]);

  // Fetch companies for dropdown
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get("/api/company/all");
      const companies = response.data.data;
      setCompaniesOptions(
        companies.map((company) => ({
          label: company.name,
          value: company.id,
        }))
      );
    } catch (error) {
      console.error("Error fetching companies:", error);
      context.notification.show("Failed to load companies", "error", true, "toast", "x-circle");
    }
  }, [context.notification]);

  useEffect(() => {
    if (showModal) {
      fetchAgencies();
      fetchCompanies();
    }
  }, [showModal, fetchAgencies, fetchCompanies]);

  // Get today's date in YYYY-MM-DD format for default effective date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  return (
    <Fragment>
      {showModal && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            {loading ? (
              <img
                src={Loader}
                alt="loader"
                style={{ width: 20, marginTop: 170, marginBottom: 170 }}
              />
            ) : (
              <div className={styles.insideWrapper}>
                <div className={styles.title}>Create Policy</div>
                <div className={styles.line} />
                <Formik
                  validationSchema={validationSchema}
                  initialValues={{
                    agency_id: "",
                    company_id: "",
                    insured_name: "",
                    policy_number: "",
                    carrier: "",
                    premium_amount: "",
                    effective_date: getTodayDate(),
                  }}
                  onSubmit={async (values) => {
                    setSubmitting(true);

                    try {
                      const policyData = {
                        account_id: values.agency_id,
                        company_id: values.company_id,
                        submission_number: values.policy_number,
                        carrier: values.carrier,
                        premium: parseFloat(values.premium_amount),
                        effective_date: values.effective_date,
                        insured_name: values.insured_name,
                        uploaded_by: "Admin User", // TODO: Get actual user name
                      };

                      await axios.post("/api/policy/create", policyData);

                      setSubmitting(false);
                      setShowModal(false);

                      // Show different message based on premium amount
                      const premiumAmount = parseFloat(values.premium_amount);
                      const message = premiumAmount === 0 
                        ? "Policy created successfully with inactive status (due to $0 premium)!"
                        : "Policy created successfully!";

                      context.notification.show(
                        message,
                        "success",
                        true,
                        "toast",
                        "check-circle"
                      );

                      // Refresh the policies table
                      if (onPolicyCreated) {
                        onPolicyCreated();
                      }
                    } catch (error) {
                      console.error("Error creating policy:", error);
                      setSubmitting(false);

                      const errorMessage =
                        error.response?.data?.message || "Failed to create policy";
                      context.notification.show(errorMessage, "error", true, "toast", "x-circle");
                    }
                  }}
                >
                  {({
                    errors,
                    touched,
                    values,
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                  }) => (
                    <Fragment>
                      <Form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.fieldRow}>
                          <FieldInput
                            id="agency_id"
                            type="select"
                            label="Agency Name"
                            value={values.agency_id}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.agency_id}
                            touched={touched.agency_id}
                            options={agenciesOptions}
                          />
                          <FieldInput
                            id="company_id"
                            type="select"
                            label="Company"
                            value={values.company_id}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.company_id}
                            touched={touched.company_id}
                            options={companiesOptions}
                          />
                        </div>

                        <div className={styles.fieldRow}>
                          <FieldInput
                            id="insured_name"
                            type="text"
                            label="Insured Name"
                            value={values.insured_name}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.insured_name}
                            touched={touched.insured_name}
                          />
                          <FieldInput
                            id="policy_number"
                            type="text"
                            label="Policy Number"
                            value={values.policy_number}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.policy_number}
                            touched={touched.policy_number}
                          />
                        </div>

                        <div className={styles.fieldRow}>
                          <FieldInput
                            id="carrier"
                            type="select"
                            label="Carrier"
                            value={values.carrier}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.carrier}
                            touched={touched.carrier}
                            options={CARRIER_OPTIONS}
                          />
                          <FieldInput
                            id="premium_amount"
                            type="number"
                            label="Premium Amount"
                            value={values.premium_amount}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.premium_amount}
                            touched={touched.premium_amount}
                          />
                        </div>

                        <div style={{ fontSize: '12px', color: '#666', marginTop: '-15px', marginBottom: '15px', fontStyle: 'italic', textAlign: 'right' }}>
                          💡 Policies with $0 premium will be created with inactive status
                        </div>

                        <div className={styles.fieldRow}>
                          <FieldInput
                            id="effective_date"
                            type="date"
                            label="Effective Date"
                            value={values.effective_date}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            required
                            showErrors
                            errors={errors.effective_date}
                            touched={touched.effective_date}
                          />
                        </div>

                        <div className={styles.footer}>
                          <div className={styles.buttons}>
                            <div className={styles.cancel} onClick={() => setShowModal(false)}>
                              Cancel
                            </div>
                            <div
                              className={styles.save}
                              onClick={handleSubmit}
                              style={{
                                opacity: submitting ? 0.7 : 1,
                                cursor: submitting ? "not-allowed" : "pointer",
                              }}
                            >
                              {submitting ? (
                                <img src={Loader} alt="loader" className={styles.loader} />
                              ) : (
                                "Create Policy"
                              )}
                            </div>
                          </div>
                        </div>
                      </Form>
                    </Fragment>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}
